import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { register } from '@/utils/auth'

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming',
].map(s => ({ value: s, label: s }))

const SITUATIONS = [
  { value: 'divorcing',   emoji: '⚖️', label: 'Going through divorce' },
  { value: 'custody',     emoji: '👨‍👧', label: 'Custody dispute' },
  { value: 'cps',         emoji: '🏛️', label: 'CPS involvement' },
  { value: 'coparenting', emoji: '🤝', label: 'Managing co-parenting' },
]

const STEPS = 3

export default function Register() {
  const navigate = useNavigate()
  const { loadUserFromApi } = useApp()

  const [step, setStep]   = useState(1)
  const [form, setForm]   = useState({ name: '', email: '', password: '', state: '', situation: '' })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const next = () => { setError(null); setStep(s => s + 1) }
  const back = () => step > 1 ? setStep(s => s - 1) : navigate('/')

  const finish = async () => {
    setError(null)
    setLoading(true)
    try {
      const user = await register(form)
      await loadUserFromApi(user)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err.message.includes('exists') ? 'That email is already registered. Try signing in.' : err.message)
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-white lg:bg-slate-100 lg:px-6 lg:py-10">
      <div className="flex flex-col h-full bg-white lg:max-w-2xl lg:mx-auto lg:w-full lg:rounded-3xl lg:overflow-hidden lg:shadow-xl lg:border lg:border-slate-200">

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pb-2 flex-shrink-0" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
        <button onClick={back} className="text-slate-500 hover:text-slate-600 tap-target" aria-label="Go back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className="text-xs text-slate-500 font-medium">Step {step} of {STEPS}</span>
        <Link to="/login" className="text-xs text-brand-700 font-semibold hover:underline tap-target">
          Sign in
        </Link>
      </div>

      {/* Progress */}
      <div className="px-6 pb-2">
        <div className="flex gap-1.5">
          {Array.from({ length: STEPS }).map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < step ? 'bg-brand-500' : 'bg-slate-200'}`} />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar">

        {/* Step 1: Credentials */}
        {step === 1 && (
          <div className="flex flex-col gap-6 px-6 py-8 animate-slide-up">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Create your account</h2>
              <p className="text-sm text-slate-500 mt-1">Your data stays private and secure.</p>
            </div>
            <div className="flex flex-col gap-4">
              <Input label="Your first name" type="text" value={form.name}
                onChange={e => set('name', e.target.value)} placeholder="e.g. Alex" required />
              <Input label="Email address" type="email" value={form.email}
                onChange={e => set('email', e.target.value)} placeholder="your@email.com"
                required autoComplete="email" />
              <Input label="Password" type="password" value={form.password}
                onChange={e => set('password', e.target.value)} placeholder="Min. 8 characters"
                required hint="At least 8 characters." autoComplete="new-password" />
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>}
            <Button variant="primary" size="lg" fullWidth
              disabled={!form.name.trim() || !form.email || form.password.length < 8}
              onClick={next}>
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: State */}
        {step === 2 && (
          <div className="flex flex-col gap-6 px-6 py-8 animate-slide-up">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Your state</h2>
              <p className="text-sm text-slate-500 mt-1">Custody laws vary significantly by state.</p>
            </div>
            <Input label="Which state are you in?" type="select" value={form.state}
              onChange={e => set('state', e.target.value)} placeholder="Select your state..."
              options={US_STATES} required />
            {form.state && (
              <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 text-sm text-brand-700">
                📍 <strong>{form.state}</strong> — AI guidance will be tailored to {form.state} family law.
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="ghost" size="md" className="flex-1" onClick={back}>Back</Button>
              <Button variant="primary" size="md" className="flex-1" disabled={!form.state} onClick={next}>Continue</Button>
            </div>
          </div>
        )}

        {/* Step 3: Situation */}
        {step === 3 && (
          <div className="flex flex-col gap-6 px-6 py-8 animate-slide-up">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Your situation</h2>
              <p className="text-sm text-slate-500 mt-1">Select what best describes where you are right now.</p>
            </div>
            <div className="flex flex-col gap-3">
              {SITUATIONS.map(s => (
                <button key={s.value} onClick={() => set('situation', s.value)}
                  className={[
                    'w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all',
                    form.situation === s.value ? 'border-brand-500 bg-brand-50' : 'border-slate-200 bg-white hover:border-brand-300',
                  ].join(' ')}>
                  <span className="text-2xl flex-shrink-0">{s.emoji}</span>
                  <span className="font-semibold text-slate-800 text-sm">{s.label}</span>
                  {form.situation === s.value && (
                    <div className="ml-auto w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} className="w-3 h-3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>}
            <div className="flex gap-3">
              <Button variant="ghost" size="md" className="flex-1" onClick={back}>Back</Button>
              <Button variant="primary" size="md" className="flex-1"
                loading={loading} disabled={!form.situation}
                onClick={finish}>
                Create Account
              </Button>
            </div>
          </div>
        )}

      </div>
      </div>
    </div>
  )
}
